import _uniqBy from 'lodash/uniqBy';
import { buildTableData } from './buildTableData';
import { useOrderByTableData } from '../../useOrderByTableData';
import { usePagination } from '@lifeomic/phc-web-toolkit/src/services/util/usePagination';
import * as React from 'react';
import GenomicsService from '@lifeomic/phc-web-toolkit/src/services/GenomicsService';
import { VariantsContentProps } from '../types';
import VariantContent from '../VariantContent';

function dedupeGenesFilter(filters: any) {
  if (!filters.gene) {
    return filters;
  }

  const newFilters = { ...filters };
  const genes = newFilters.gene.split(','); // genes come to us in CSV format
  const uniqueGenes = [...new Set(genes)];
  newFilters.gene = uniqueGenes.join(','); // convert back to CSV format
  return newFilters;
}

export const jaxKnowledgeRank = (x: string | undefined): number => {
  switch (x) {
    case 'impact':
      return 0;
    case 'variant':
      return 1;
    case 'gene':
      return 2;
    default:
      return 3;
  }
};

export const getMostSignificantTranscript = (value: any) => {
  const transcripts = value?.ensembl?.sort(
    (a: any, b: any) =>
      jaxKnowledgeRank(a?.jaxKnowledge) - jaxKnowledgeRank(b?.jaxKnowledge),
  );
  return transcripts?.[0];
};

/**
 * The JAX cancer effect (jaxKnowledge and proteinEffect) can be on any
 * transcript because JAX catalogs their data based on the amino acid change
 * and the number of the AA change depends on the transcript. So we have to
 * look through all the transcripts and find the most significant jax result
 * (in practice there is only ever one jax cancer effect per variant, but
 * just in case we handle multiples) and then lift that up as top level
 * properties on the variant so they can be sorted and displayed easily
 * without a lot of searching when rendering.
 * @param variants
 * @returns
 */
export const liftJaxFields = (variants: any[] | undefined): any[] => {
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  return variants?.map(v => {
    const mostSignificantTranscript = getMostSignificantTranscript(v);
    return {
      ...v,
      jaxKnowledge: mostSignificantTranscript?.jaxKnowledge,
      proteinEffect: mostSignificantTranscript?.proteinEffect,
    };
  });
};

const genomicsService = new GenomicsService();

export const ShortVariantsContent: React.FC<VariantsContentProps> = ({
  onRowClick,
  fetchPrependedColumns,
}) => {
  const {
    hasMoreToLoad,
    isLoading,
    loadMore,
    makeFetchRequest,
    results,
    lastResponse,
    error,
    resetResults,
  } = usePagination(genomicsService.getShortVariants);

  const resultsWithLiftedJaxFields = React.useMemo(
    () => liftJaxFields(results),
    [results],
  );

  const { handleSortClicked, sortedData } = useOrderByTableData(
    resultsWithLiftedJaxFields,
  );

  return (
    <VariantContent
      onRowClick={onRowClick}
      fetchPrependedColumns={fetchPrependedColumns}
      setType={'shortVariant'}
      isLoading={isLoading}
      hasMoreToLoad={hasMoreToLoad}
      loadMore={loadMore}
      sortedData={_uniqBy(sortedData, 'id')}
      handleSortClicked={handleSortClicked}
      resetResults={resetResults}
      makeFetchRequest={makeFetchRequest}
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      lastResponse={lastResponse}
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      error={error}
      buildTableData={buildTableData}
      additionalFetchFilter={dedupeGenesFilter}
    />
  );
};
